import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {GoogleMapsService} from "./google-map.service";

/** Stato del flow di disegno poligono (POI avanzato), emesso dal service via polygonState$. */
export interface PolygonDrawState {
  pointsCount: number;
  closed: boolean;
  centroid: { lat: number; lng: number } | null;
  /** PIP del centroide: null se polygon non chiuso, true/false se chiuso. */
  centroidValid: boolean | null;
}

/* Color tokens: stessi del cerchio standard del POI (#2e97f2).
 * Servono come default ma vengono override-ati dal colore scelto dall'utente
 * (setAreaColor), persistito per POI nel campo `color`. */
const POLY_DEFAULT_COLOR = '#2e97f2';
const POLY_FILL_OPACITY_CLOSED = 0.15;
const POLY_STROKE_WEIGHT = 2;

/* Colore corrente — usato dai vari draw/redraw. Stessa hex per stroke e fill
 * (Google Maps applica le opacita' separatamente). Cambiato a runtime via
 * setAreaColor() dal page poi-add quando l'utente sceglie un nuovo valore. */
let POLY_COLOR = POLY_DEFAULT_COLOR;

@Injectable({
  providedIn: 'root'
})
export class PoiMapService {

  readonly MAP_NAME: string = "poi";
  mapType: string;
  map: any;
  poiMarker: any;
  circle: any;
  popup: any;
  static popupLabel: string;

  /* ─── Stato poligono (modalita' avanzata) ───────────────────────────── */
  polygonPoints: { lat: number; lng: number }[] = [];
  polygonClosed: boolean = false;
  private polygon: any = null;          // google.maps.Polygon (post-close, editable)
  private polygonPolyline: any = null;  // google.maps.Polyline (pre-close, dashed)
  private polygonPreviewFill: any = null; // google.maps.Polygon (pre-close, riempimento live)
  private vertexMarkers: any[] = [];    // pallini vertici grandi
  private midpointMarkers: any[] = [];  // pallini midpoint cavi (post-close)
  private polygonClickListener: any = null;
  /** Listener click sul marker centroide (attivo solo quando il poligono e' chiuso):
   *  cliccando il marker si cancella tutta l'area. */
  /** Marker "delete handle" mostrato sul primo vertice quando il poligono e' chiuso.
   *  Visualmente identico al primo vertice (pallino rosso con X). Click → clearPolygon. */
  private closedDeleteHandle: any = null;
  /** Pallino blu mostrato sull'indirizzo durante il disegno (pre-chiusura). */
  private addressDot: any = null;
  /** Ultima posizione "address" nota (aggiornata da movePoiMarker / search). */
  private addressPos: { lat: number; lng: number } | null = null;
  /** Subject che emette lo stato del poligono — il page si subscribe per la UI. */
  polygonState$ = new Subject<PolygonDrawState>();

  /** Etichette tradotte per i tooltip dei marker (impostate dal page via setPolygonLang). */
  private polyLang = {
    cancellaArea: 'Clicca per cancellare l\'area',
    inserisciVertice: 'Clicca per inserire un vertice qui'
  };

  /** Configura le label tradotte usate dai marker poligono (tooltips). */
  setPolygonLang(lang: { cancellaArea?: string; inserisciVertice?: string }){
    if(lang.cancellaArea) this.polyLang.cancellaArea = lang.cancellaArea;
    if(lang.inserisciVertice) this.polyLang.inserisciVertice = lang.inserisciVertice;
  }

  /** Imposta il colore area/bordo (stesso per cerchio standard e polygon) e
   *  ridisegna gli elementi gia' in mappa con il nuovo colore. Live-preview
   *  durante la creazione/modifica del POI. */
  setAreaColor(color: string){
    POLY_COLOR = color || POLY_DEFAULT_COLOR;
    /* Cerchio standard (modalita' circle): aggiorno strokeColor/fillColor in place. */
    if(this.circle){
      this.circle.setOptions({
        strokeColor: POLY_COLOR,
        fillColor: POLY_COLOR
      });
    }
    /* Polygon chiuso (modalita' avanzata): setOptions sulle proprieta'. */
    if(this.polygon){
      this.polygon.setOptions({
        strokeColor: POLY_COLOR,
        fillColor: POLY_COLOR
      });
    }
    /* Polyline tratteggiata + preview fill + vertex/midpoint markers: tutti
     * dipendono dal colore, ridisegno tutto. */
    if(!this.polygonClosed && this.polygonPoints.length > 0){
      this.redrawDrawing();
    } else if(this.polygonClosed){
      this.redrawVertexMarkers();
      this.redrawMidpointMarkers();
    }
  }

  constructor(
    public googleMaps: GoogleMapsService
  ) {
  }

  /** Teardown completo di tutti gli artifact del POI avanzato (polygon, polyline,
   *  preview fill, vertex/midpoint markers, address dot, closed delete handle).
   *  Va chiamato alla chiusura del modal: questi oggetti li aggiungiamo a Google
   *  Maps direttamente con `map: this.map`, quindi NON sono tracciati dal
   *  GoogleMapsService.clearMap() della pagina principale e altrimenti sopravvivono. */
  cleanupPolygonArtifacts() {
    this.uninstallMapClickListener();
    this.uninstallClosedDeleteHandle();
    if(this.polygon){
      this.polygon.setMap(null);
      this.polygon = null;
    }
    if(this.polygonPolyline){
      this.polygonPolyline.setMap(null);
      this.polygonPolyline = null;
    }
    if(this.polygonPreviewFill){
      this.polygonPreviewFill.setMap(null);
      this.polygonPreviewFill = null;
    }
    for(const m of this.vertexMarkers){ m.map = null; }
    this.vertexMarkers = [];
    for(const m of this.midpointMarkers){ m.map = null; }
    this.midpointMarkers = [];
    if(this.addressDot){
      this.addressDot.map = null;
      this.addressDot = null;
    }
    this.polygonPoints = [];
    this.polygonClosed = false;
    this.addressPos = null;
    this.setDrawingCursor(false);
  }

  /** Ri-aggancia il divMap di Google al container #poi-map (utile quando Angular
   *  distrugge/ricrea il container tornando a uno step e poi rientrando). Idempotente. */
  reattachToContainer(): boolean {
    if(!this.map) return false;
    const mapContainer: any = document.getElementById("poi-map");
    if(!mapContainer) return false;
    const div = this.googleMaps.getDivMap();
    if(div && div.parentElement !== mapContainer){
      mapContainer.appendChild(div);
    }
    this.googleMaps.setCurrentMap(this.MAP_NAME);
    // Forza un resize/redraw: Google Maps va informato del cambio di container.
    setTimeout(() => {
      try{
        GoogleMapsService.google.maps.event.trigger(this.map, 'resize');
        // Ricentro su marker corrente (o sul centroide se polygon chiuso).
        if(this.poiMarker && this.poiMarker.position){
          this.map.panTo(this.poiMarker.position);
        }
      }catch(e){}
    }, 0);
    return true;
  }

  initMap(mapType: string, popupLabel: string, lat?: number, lng?: number, radius?: number, image?: string, labelNumber?: number) {
    PoiMapService.popupLabel = popupLabel;
    this.mapType = mapType;
    this.googleMaps.clearMap();
    this.map = this.googleMaps.getMap();
    let mapContainer: any = document.getElementById("poi-map");
    if(mapContainer){
      mapContainer.appendChild(this.googleMaps.getDivMap());
      this.googleMaps.setCurrentMap(this.MAP_NAME);
    }
    this.map.setOptions({
      center: {lat: lat || 44.69, lng: lng || 7.84},
      zoom: 9,
      fullscreenControl: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: {
        style: GoogleMapsService.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: GoogleMapsService.google.maps.ControlPosition.RIGHT_BOTTOM
      },
    });
    this.poiMarker = undefined;
    this.circle = undefined;
    this.drawPoiMarker(lat || 44.7030, lng || 7.8395, radius, image, labelNumber);
  }

  drawPoiMarker(lat: number, lng: number, radius: number, image?: string, labelNumber?: number){
    const coord: any = {
      lat: lat,
      lng: lng
    };

    let marker: any = new GoogleMapsService.google.maps.marker.AdvancedMarkerElement({
      zIndex: 1,
      map: this.map,
      position: { lat, lng },
      content: GoogleMapsService.createPoiMarker(),
      gmpDraggable: true
    });
    marker.id = 'poi';
    marker.type = 'poi';
    /* Per POI poligonali radius arriva null (non c'e' raggio): uso un default
     * tecnico per non rompere google.maps.Circle (verra' nascosto da loadPolygon).  */
    let circle = new GoogleMapsService.google.maps.Circle({
      center: coord,
      radius: radius || 100,
      strokeColor: POLY_COLOR,
      strokeWeight: 2,
      fillColor: POLY_COLOR,
      fillOpacity: POLY_FILL_OPACITY_CLOSED,
      id: 'poi'
    });
    marker.addListener('dragend', () => {
      if(this.circle) this.circle.setCenter(marker.position);
      if(this.popup) this.popup.position = marker.position;
    });
    this.poiMarker = marker;
    this.updateImage(image, labelNumber)
    this.circle = circle;
    this.googleMaps.addCircle(circle, this.MAP_NAME);
    this.googleMaps.addMarker(marker, this.MAP_NAME);
    this.popup = this.createPopupGoogle(marker.position);
    this.googleMaps.addPopup(this.popup, this.MAP_NAME);
    this.fitCenterGoogle();
  }

  cleanPoiMarkers(){
    switch (this.mapType) {
      case "Google":
        this.cleanPoiMarkeGoogle();
        break;
    }
  }

  movePoiMarker(lat: number | string, lng: number | string){
    // I chiamanti spesso passano valori che arrivano dal backend come stringhe
    // (es. LocationIQ ritorna lat/lon come string). gmp-advanced-marker pretende
    // un number puro su .position.lat/lng, quindi normalizzo qui una volta sola.
    const latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
    const lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;
    if(!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;
    switch (this.mapType) {
      case "Google":
        this.movePoiMarkerGoogle(latNum, lngNum);
        break;
    }
  }

  changeCircleRadius(radius: number){
    switch (this.mapType) {
      case "Google":
        this.changeCircleRadiusGoogle(radius);
        break;
    }
  }

  private cleanPoiMarkeGoogle(){
    if(this.poiMarker){
      this.googleMaps.removeMarker(this.poiMarker, this.MAP_NAME);
    }
    if(this.circle){
      this.googleMaps.removeMarker(this.circle, this.MAP_NAME);
    }
    if(this.popup){
      this.googleMaps.removePopup(this.popup, this.MAP_NAME);
    }
  }

  updateImage(image: string, label_number: number){
    if(!this.poiMarker) return;
    let element: any = this.poiMarker.content.querySelector('.mappa_internal_img');
    const etichetta: any = this.poiMarker.content.querySelector('.mappa_etichetta_number');
    element.style.backgroundImage = `url(${image})`;
    if(image){
      element.style.backgroundImage = `url(${image})`;
      if(label_number){
        etichetta.textContent = `${label_number || ''}`;
        etichetta.style.display = 'block';
      }
    }else{
      element.style.backgroundImage = 'none';
      etichetta.style.display = 'none';
      element = this.poiMarker.content.querySelector('.mappa_label_number');
      element.textContent = `${label_number || ''}`;
    }
  }

  private createPopupGoogle(position: any): any{
    class Popup extends GoogleMapsService.google.maps.OverlayView {
      position: any;
      containerDiv: HTMLDivElement;
      id: string;

      constructor(position: any, content: HTMLElement, id: string) {
        super();
        this.position = position;
        this.id = id;

        content.classList.add("map-poi-popup-bubble");

        const bubbleAnchor = document.createElement("div");
        bubbleAnchor.classList.add("map-poi-popup-bubble-anchor");
        bubbleAnchor.appendChild(content);

        this.containerDiv = document.createElement("div");
        this.containerDiv.classList.add("map-poi-popup-container");
        this.containerDiv.appendChild(bubbleAnchor);

        Popup.preventMapHitsAndGesturesFrom(this.containerDiv);
      }

      onAdd() {
        this.getPanes().floatPane.appendChild(this.containerDiv);
      }

      onRemove() {
        if (this.containerDiv.parentElement) {
          this.containerDiv.parentElement.removeChild(this.containerDiv);
        }
      }

      draw() {
        const divPosition = this.getProjection().fromLatLngToDivPixel(
          this.position
        );

        const display =
          Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
            ? "block"
            : "none";

        if (display === "block") {
          this.containerDiv.style.left = divPosition.x + "px";
          this.containerDiv.style.top = divPosition.y + "px";
        }

        if (this.containerDiv.style.display !== display) {
          this.containerDiv.style.display = display;
        }
      }
    }

    let element: HTMLElement = document.createElement('div');
    element.innerText = PoiMapService.popupLabel;
    return new Popup(
      position,
      element,
      'label'
    );
  }

  private movePoiMarkerGoogle(lat: number, lng: number){
    const position: any = new GoogleMapsService.google.maps.LatLng(lat, lng);
    // Aggiorno la posizione "address" — usata dal pallino in modalita' avanzata.
    this.addressPos = { lat, lng };
    // In modalita' poligono *non* chiusa: muovo solo il pallino, il marker label
    // resta nascosto (apparira' al centroide alla chiusura).
    const inPolyDrawing = (this.polygonClickListener || this.polygonPoints.length > 0) && !this.polygonClosed;
    if(inPolyDrawing){
      if(this.addressDot) this.addressDot.position = { lat, lng };
      else this.showAddressDot();
      // Centro la mappa sul nuovo indirizzo (no fitBounds del cerchio in questo caso).
      if(this.map && this.map.panTo) this.map.panTo({ lat, lng });
      return;
    }
    // Flusso normale (modalita' cerchio o poligono gia' chiuso).
    if(this.poiMarker) this.poiMarker.position = position;
    if(this.circle) this.circle.setCenter(position);
    if(this.popup) this.popup.position = position;
    this.fitCenterGoogle();
  }

  private changeCircleRadiusGoogle(radius: number){
    if(this.circle){
      this.circle.setRadius(radius);
      this.fitCenterGoogle();
    }
  }

  private fitCenterGoogle(){
    if(!this.circle) return;
    let bounds: any = this.circle.getBounds();
    /* Per POI poligonali il radius e' null/0 → getBounds() torna null. Esco.
     * (Il fit sul poligono lo fa fitPolygonBoundsGoogle dopo loadPolygon.) */
    if(!bounds || bounds.isEmpty === undefined) return;
    if(!bounds.isEmpty()){
      this.map.fitBounds(bounds);
      setTimeout(() => {
        if(this.map.getZoom() > 16){
          this.map.setZoom(16);
        }
      }, 200)
    }
  }

  /** Fit della mappa sui vertici del poligono corrente. Usato in edit per
   *  partire zoomato sull'area. */
  private fitPolygonBoundsGoogle(){
    if(!this.map || this.polygonPoints.length < 1) return;
    const bounds = new GoogleMapsService.google.maps.LatLngBounds();
    for(const p of this.polygonPoints){
      bounds.extend({ lat: p.lat, lng: p.lng });
    }
    if(bounds.isEmpty()) return;
    this.map.fitBounds(bounds, 40 /* px padding */);
    setTimeout(() => {
      if(this.map.getZoom() > 17){
        this.map.setZoom(17);
      }
    }, 200);
  }


  /* ════════════════════════════════════════════════════════════════════════════════
   * MODALITA' POLIGONO (POI avanzato) — flow Variante B Claude Design
   *
   * Enter -> nasconde cerchio, disabilita drag del marker, abilita click-to-add.
   * AddPoint sulla mappa fino a chiusura. Closed -> Polygon editable nativo Google,
   * Reopen -> torna a Polyline tratteggiata click-to-add. Marker label si sposta sul
   * centroide ad ogni cambio.
   * ════════════════════════════════════════════════════════════════════════════════ */

  /** Attiva la modalita' poligono: cleanup cerchio, abilita click sulla mappa per
   *  aggiungere vertici, prepara il marker label come centroide. */
  enterPolygonMode() {
    if(!this.map) return;
    // Cerchio off + marker label nascosto (lo riportiamo alla chiusura sul centroide).
    if(this.circle) this.circle.setVisible(false);
    if(this.poiMarker) this.poiMarker.gmpDraggable = false;
    // Memorizzo la posizione dell'indirizzo dal marker corrente (e' il punto geocoded).
    if(this.poiMarker && this.poiMarker.position){
      const pos = this.poiMarker.position;
      this.addressPos = {
        lat: typeof pos.lat === 'function' ? pos.lat() : pos.lat,
        lng: typeof pos.lng === 'function' ? pos.lng() : pos.lng
      };
    }
    // Inizializzo stato poligono pulito.
    this.polygonPoints = [];
    this.polygonClosed = false;
    this.removePolygonRender();
    this.installMapClickListener();
    // Marker label off, mostro solo il pallino blu sull'indirizzo.
    this.hideLabelMarker();
    this.showAddressDot();
    // Cursore "pointer" (mano-clic) per indicare che si possono aggiungere vertici.
    this.setDrawingCursor(true);
    this.emitPolygonState();
  }

  /** Esce dalla modalita' poligono: rimuove il poligono dalla mappa, riattiva il cerchio. */
  exitPolygonMode() {
    this.uninstallMapClickListener();
    this.uninstallClosedDeleteHandle();
    this.removePolygonRender();
    this.removeAddressDot();
    this.setDrawingCursor(false);
    this.polygonPoints = [];
    this.polygonClosed = false;
    if(this.circle) this.circle.setVisible(true);
    if(this.poiMarker){
      this.poiMarker.gmpDraggable = true;
      // Riposiziono marker sul centro del cerchio (posizione originale).
      if(this.circle){
        const c = this.circle.getCenter();
        if(c) this.poiMarker.position = { lat: c.lat(), lng: c.lng() };
      }
      this.showLabelMarker();
    }
    this.emitPolygonState();
  }

  /** Aggiunge un vertice (chiamato dal listener click sulla mappa). */
  addPolygonPoint(lat: number, lng: number) {
    if(this.polygonClosed) return;
    this.polygonPoints.push({ lat, lng });
    this.redrawDrawing();
    // Pre-chiusura: nessun marker centroide. Resta solo il pallino blu sull'indirizzo.
    this.emitPolygonState();
  }

  /** Rimuove l'ultimo vertice. Se il poligono era chiuso, viene riaperto. */
  removeLastPoint() {
    if(!this.polygonPoints.length) return;
    this.polygonPoints.pop();
    if(this.polygonClosed){
      // Torna in modalita' "open" (polyline tratteggiata): label off, pallino on.
      this.polygonClosed = false;
      this.removePolygonRender();
      this.hideLabelMarker();
      this.showAddressDot();
      this.uninstallClosedDeleteHandle();
    }
    this.redrawDrawing();
    this.emitPolygonState();
  }

  /** Svuota tutti i vertici, esce dalla modalita' chiusa. */
  clearPolygon() {
    this.polygonPoints = [];
    this.polygonClosed = false;
    this.removePolygonRender();
    this.uninstallClosedDeleteHandle();
    // Label off (siamo in disegno) + pallino visibile sull'indirizzo.
    this.hideLabelMarker();
    this.showAddressDot();
    this.emitPolygonState();
  }

  /** Chiude il poligono (richiede ≥3 punti). Sostituisce la polyline con un Polygon editable. */
  closePolygon() {
    if(this.polygonPoints.length < 3 || this.polygonClosed) return;
    this.polygonClosed = true;
    // Cleanup della fase "drawing": polyline tratteggiata e preview fill (il Polygon
    // ora ha il proprio fill/stroke). I pallini verdi grandi invece li RI-disegniamo
    // qui sotto in versione "closed" (idx 0 escluso, ci pensa il closedDeleteHandle).
    if(this.polygonPolyline){
      this.polygonPolyline.setMap(null);
      this.polygonPolyline = null;
    }
    if(this.polygonPreviewFill){
      this.polygonPreviewFill.setMap(null);
      this.polygonPreviewFill = null;
    }
    this.removeVertexMarkers();
    // Polygon NON editable: niente handle nativi piccoli, niente midpoint.
    // L'editing avviene tramite i nostri pallini verdi grandi (gmpDraggable).
    this.polygon = new GoogleMapsService.google.maps.Polygon({
      paths: this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })),
      strokeColor: POLY_COLOR,
      strokeOpacity: 0.95,
      strokeWeight: POLY_STROKE_WEIGHT,
      fillColor: POLY_COLOR,
      fillOpacity: POLY_FILL_OPACITY_CLOSED,
      editable: false,
      draggable: false,
      clickable: false,
      map: this.map
    });
    // Alla chiusura: via il pallino blu, on il marker label sul centroide.
    this.removeAddressDot();
    this.updateCentroidMarker();
    // Pallino rosso con X sul primo vertice (delete handle).
    this.installClosedDeleteHandle();
    // Ri-creo i pallini verdi grandi (saltando idx 0) + i midpoint cavi.
    this.redrawVertexMarkers();
    this.redrawMidpointMarkers();
    // Tornare al cursore di default (non si aggiungono piu' vertici cliccando).
    this.setDrawingCursor(false);
    this.emitPolygonState();
  }

  /** Riapre un poligono chiuso: rimuove l'editing nativo, ripristina la polyline tratteggiata. */
  reopenPolygon() {
    if(!this.polygonClosed) return;
    this.polygonClosed = false;
    if(this.polygon){
      this.polygon.setMap(null);
      this.polygon = null;
    }
    this.redrawDrawing();
    // Torna alla fase disegno: label off, pallino on sull'indirizzo.
    this.hideLabelMarker();
    this.showAddressDot();
    // Disabilito il click-to-delete sul centroide (non e' piu' chiuso).
    this.uninstallClosedDeleteHandle();
    // Cursore pointer (di nuovo in fase di aggiunta vertici).
    this.setDrawingCursor(true);
    this.emitPolygonState();
  }

  /** Calcola il centroide geometrico (signed area). Lat/lng trattati come piani —
   *  errore trascurabile per scale tipiche di POI (decine/centinaia di metri). */
  calculateCentroid(pts: { lat: number; lng: number }[]): { lat: number; lng: number } | null {
    if(!pts || pts.length === 0) return null;
    if(pts.length === 1) return { ...pts[0] };
    if(pts.length === 2) return {
      lat: (pts[0].lat + pts[1].lat) / 2,
      lng: (pts[0].lng + pts[1].lng) / 2
    };
    let area = 0, cx = 0, cy = 0;
    const n = pts.length;
    for(let i = 0; i < n; i++){
      const j = (i + 1) % n;
      const xi = pts[i].lng, yi = pts[i].lat;
      const xj = pts[j].lng, yj = pts[j].lat;
      const cross = xi * yj - xj * yi;
      area += cross;
      cx += (xi + xj) * cross;
      cy += (yi + yj) * cross;
    }
    area = area / 2;
    if(area === 0){
      // Caso degenere (vertici collineari): fallback alla media dei vertici.
      let sx = 0, sy = 0;
      for(const p of pts){ sx += p.lng; sy += p.lat; }
      return { lat: sy / pts.length, lng: sx / pts.length };
    }
    cx = cx / (6 * area);
    cy = cy / (6 * area);
    return { lat: cy, lng: cx };
  }

  /** Restituisce il poligono corrente come GeoJSON Polygon (con ring chiuso).
   *  null se polygon non chiuso o meno di 3 vertici. */
  getGeoJsonPolygon(): { type: 'Polygon'; coordinates: number[][][] } | null {
    if(!this.polygonClosed || this.polygonPoints.length < 3) return null;
    const ring: number[][] = this.polygonPoints.map(p => [p.lng, p.lat]);
    // Chiusura del ring: GeoJSON richiede che il primo punto venga ripetuto alla fine.
    ring.push([this.polygonPoints[0].lng, this.polygonPoints[0].lat]);
    return { type: 'Polygon', coordinates: [ring] };
  }

  /** Restituisce il centroide del poligono corrente (o null se non disegnato). */
  getCurrentCentroid(): { lat: number; lng: number } | null {
    return this.calculateCentroid(this.polygonPoints);
  }

  /** Carica un poligono pre-esistente (in modifica) come stato "chiuso", pronto a editare.
   *  vertices: array di {lat,lng} (senza ring closure duplicata, GeoJSON style escluso ultimo punto). */
  loadPolygon(vertices: { lat: number; lng: number }[]): void {
    if(!vertices || vertices.length < 3) return;
    // Setup come post-chiusura ma senza passare dal click-flow.
    this.polygonPoints = vertices.map(v => ({ lat: v.lat, lng: v.lng }));
    this.polygonClosed = true;
    // Cleanup eventuali render precedenti, poi disegno tutto in versione "closed".
    this.removePolygonRender();
    this.removeAddressDot();
    this.polygon = new GoogleMapsService.google.maps.Polygon({
      paths: this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })),
      strokeColor: POLY_COLOR,
      strokeOpacity: 0.95,
      strokeWeight: POLY_STROKE_WEIGHT,
      fillColor: POLY_COLOR,
      fillOpacity: POLY_FILL_OPACITY_CLOSED,
      editable: false,
      draggable: false,
      clickable: false,
      map: this.map
    });
    if(this.circle) this.circle.setVisible(false);
    if(this.poiMarker) this.poiMarker.gmpDraggable = false;
    this.updateCentroidMarker();
    this.installClosedDeleteHandle();
    this.redrawVertexMarkers();
    this.redrawMidpointMarkers();
    /* Map click listener installato anche se il poligono e' gia' chiuso: e' no-op
     * mentre closed=true, ma serve per gestire i click sul map quando l'utente
     * preme "Riapri per modificare". */
    this.installMapClickListener();
    this.setDrawingCursor(false);
    /* Zoom & center sull'area appena caricata (in edit l'utente parte gia' col
     * poligono in vista). */
    this.fitPolygonBoundsGoogle();
    this.emitPolygonState();
  }

  /** Point-in-polygon via ray casting. */
  isPointInPolygon(point: { lat: number; lng: number }, pts: { lat: number; lng: number }[]): boolean {
    if(!point || !pts || pts.length < 3) return false;
    let inside = false;
    const x = point.lng, y = point.lat;
    for(let i = 0, j = pts.length - 1; i < pts.length; j = i++){
      const xi = pts[i].lng, yi = pts[i].lat;
      const xj = pts[j].lng, yj = pts[j].lat;
      const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if(intersect) inside = !inside;
    }
    return inside;
  }


  /* ─── internals: rendering, listener, sync ─────────────────────────── */

  private installMapClickListener() {
    if(this.polygonClickListener) return;
    this.polygonClickListener = this.map.addListener('click', (e: any) => {
      if(this.polygonClosed || !e.latLng) return;
      this.addPolygonPoint(e.latLng.lat(), e.latLng.lng());
    });
  }

  private uninstallMapClickListener() {
    if(this.polygonClickListener){
      GoogleMapsService.google.maps.event.removeListener(this.polygonClickListener);
      this.polygonClickListener = null;
    }
  }

  /** Cursore mappa: in fase di disegno usiamo "pointer" (mano-clic) per
   *  comunicare che si aggiunge un vertice cliccando. Restoring a default. */
  private setDrawingCursor(active: boolean){
    if(!this.map) return;
    this.map.setOptions({
      draggableCursor: active ? 'pointer' : null,
      draggingCursor: active ? 'grabbing' : null
    });
  }

  private removePolygonRender() {
    if(this.polygon){
      this.polygon.setMap(null);
      this.polygon = null;
    }
    if(this.polygonPolyline){
      this.polygonPolyline.setMap(null);
      this.polygonPolyline = null;
    }
    if(this.polygonPreviewFill){
      this.polygonPreviewFill.setMap(null);
      this.polygonPreviewFill = null;
    }
    this.removeVertexMarkers();
    this.removeMidpointMarkers();
  }

  /** Ridisegna tutto il chunk "drawing" pre-chiusura: fill overlay + polyline tratteggiata + pallini vertici. */
  private redrawDrawing() {
    this.redrawPreviewFill();
    this.redrawPolyline();
    this.redrawVertexMarkers();
  }

  /** Riempimento traslucido live: appare da 3 punti in poi (anteprima dell'area). */
  private redrawPreviewFill() {
    if(this.polygonPreviewFill){
      this.polygonPreviewFill.setMap(null);
      this.polygonPreviewFill = null;
    }
    if(this.polygonClosed || this.polygonPoints.length < 3) return;
    this.polygonPreviewFill = new GoogleMapsService.google.maps.Polygon({
      paths: this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })),
      strokeOpacity: 0,
      strokeWeight: 0,
      fillColor: POLY_COLOR,
      fillOpacity: 0.14,
      clickable: false,
      zIndex: 1,
      map: this.map
    });
  }

  /** Pallini ai vertici durante la creazione. Il primo diventa rosso da 3+ punti
   * (cliccandolo si chiude in automatico). */
  private redrawVertexMarkers() {
    this.removeVertexMarkers();
    if(!this.map || !this.polygonPoints.length) return;
    const canClose = this.polygonPoints.length >= 3;
    this.polygonPoints.forEach((p, idx) => {
      // Post-chiusura il primo vertice e' gestito dal closedDeleteHandle (X rossa).
      if(this.polygonClosed && idx === 0) return;
      const isFirst = idx === 0;
      const closingPoint = !this.polygonClosed && isFirst && canClose;
      const size = 22;
      const color = closingPoint ? '#dc2626' : POLY_COLOR;

      const wrap = document.createElement('div');
      wrap.style.cssText = 'position:relative; width:0; height:0;';

      // Anello pulsante (solo sul punto di chiusura), sotto il dot per non rubargli i click.
      if(closingPoint){
        const ring = document.createElement('div');
        ring.style.cssText = [
          'position:absolute',
          `left:-${size/2 + 6}px`,
          `bottom:-${size/2 + 6}px`,
          `width:${size + 12}px`,
          `height:${size + 12}px`,
          'border-radius:50%',
          `border:2px solid ${color}`,
          'opacity:0.55',
          'pointer-events:none'
        ].join(';');
        wrap.appendChild(ring);
      }

      const inner = document.createElement('div');
      inner.style.cssText = [
        'position:absolute',
        `left:-${size/2}px`,
        `bottom:-${size/2}px`,
        `width:${size}px`,
        `height:${size}px`,
        'border-radius:50%',
        `background:${color}`,
        'border:3px solid #fff',
        'box-shadow:0 2px 8px rgba(0,0,0,0.35)',
        'box-sizing:border-box',
        'cursor:grab',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'color:#fff',
        'font-weight:900',
        'font-size:13px',
        'line-height:1',
        // Glyph leggermente piu' alto per ottica
        'padding-bottom:1px',
        'transition:background 0.2s'
      ].join(';');
      // Pre-chiusura: il primo punto e' solo rosso pieno (nessuna X).
      // La X compare solo dopo la chiusura, sul closedDeleteHandle.
      wrap.appendChild(inner);

      const marker = new GoogleMapsService.google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: { lat: p.lat, lng: p.lng },
        content: wrap,
        zIndex: 3 + (closingPoint ? 1 : 0),
        gmpClickable: true,   // intercetta sempre i click (no duplicate vertex)
        gmpDraggable: true    // ogni vertice e' trascinabile
      });

      marker.addListener('gmp-click', () => {
        if(closingPoint){
          this.closePolygon();
        }
        // Altrimenti consumiamo il click senza fare nulla.
      });

      // Live update durante il drag: aggiorno la geometria (polyline+fill se aperto,
      // path del Polygon se chiuso) SENZA ricreare i vertex markers (perderei il
      // riferimento al marker in drag).
      const onDrag = (e: any) => {
        if(!e || !e.latLng) return;
        this.polygonPoints[idx] = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        if(this.polygonClosed){
          if(this.polygon){
            this.polygon.setPaths(this.polygonPoints.map(pp => ({ lat: pp.lat, lng: pp.lng })));
          }
          this.updateCentroidMarker();
          this.updateClosedDeleteHandlePosition();
          this.redrawMidpointMarkers();
        } else {
          this.redrawPreviewFill();
          this.redrawPolyline();
        }
      };
      marker.addListener('drag', onDrag);
      marker.addListener('dragend', (e: any) => {
        onDrag(e);
        this.emitPolygonState();
      });

      this.vertexMarkers.push(marker);
    });
  }

  private removeVertexMarkers() {
    for(const m of this.vertexMarkers){
      m.map = null;
    }
    this.vertexMarkers = [];
  }

  /** Midpoint markers (post-chiusura): pallini cavi su ogni segmento. Click →
   *  inserisce un nuovo vertice in quella posizione. */
  private redrawMidpointMarkers() {
    this.removeMidpointMarkers();
    if(!this.polygonClosed || !this.map || this.polygonPoints.length < 3) return;
    const n = this.polygonPoints.length;
    for(let i = 0; i < n; i++){
      const a = this.polygonPoints[i];
      const b = this.polygonPoints[(i + 1) % n];
      const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
      const size = 14;

      const wrap = document.createElement('div');
      wrap.style.cssText = 'position:relative; width:0; height:0;';
      const inner = document.createElement('div');
      inner.title = this.polyLang.inserisciVertice;
      inner.style.cssText = [
        'position:absolute',
        `left:-${size/2}px`,
        `bottom:-${size/2}px`,
        `width:${size}px`,
        `height:${size}px`,
        'border-radius:50%',
        'background:#ffffff',
        `border:2px solid ${POLY_COLOR}`,
        'box-shadow:0 1px 4px rgba(0,0,0,0.18)',
        'box-sizing:border-box',
        'cursor:pointer',
        'opacity:0.85',
        'transition:transform 0.12s, opacity 0.12s'
      ].join(';');
      inner.addEventListener('mouseenter', () => {
        inner.style.transform = 'scale(1.25)';
        inner.style.opacity = '1';
      });
      inner.addEventListener('mouseleave', () => {
        inner.style.transform = '';
        inner.style.opacity = '0.85';
      });
      wrap.appendChild(inner);

      const insertAt = i + 1; // inserisco tra i e i+1
      const marker = new GoogleMapsService.google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: mid,
        content: wrap,
        zIndex: 2,
        gmpClickable: true,
        gmpDraggable: true
      });

      // Drag: appena inizio a trascinare il midpoint, inserisco il vertice in
      // polygonPoints e da li' in poi il marker rappresenta quel vertice.
      // Comportamento identico ai midpoint handle nativi di Google.
      let insertedIdx = -1;
      let inserted = false;
      marker.addListener('dragstart', () => {
        this.polygonPoints.splice(insertAt, 0, { lat: mid.lat, lng: mid.lng });
        insertedIdx = insertAt;
        inserted = true;
        if(this.polygon){
          this.polygon.setPaths(this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })));
        }
      });
      marker.addListener('drag', (e: any) => {
        if(!inserted || !e || !e.latLng) return;
        this.polygonPoints[insertedIdx] = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        if(this.polygon){
          this.polygon.setPaths(this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })));
        }
        this.updateCentroidMarker();
        this.updateClosedDeleteHandlePosition();
        // Non ricreo i markers durante il drag (perderei il riferimento al
        // marker che sto trascinando). I midpoint vicini sono comunque obsoleti
        // ma li rifaccio a dragend.
      });
      marker.addListener('dragend', (e: any) => {
        if(inserted && e && e.latLng){
          this.polygonPoints[insertedIdx] = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          if(this.polygon){
            this.polygon.setPaths(this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })));
          }
        }
        // Fine drag: ricostruisco vertex+midpoint markers per riflettere la nuova geometria.
        this.redrawVertexMarkers();
        this.redrawMidpointMarkers();
        this.updateCentroidMarker();
        this.updateClosedDeleteHandlePosition();
        this.emitPolygonState();
      });

      // Click "secco" (senza drag): inserisce il vertice esattamente al midpoint.
      marker.addListener('gmp-click', () => {
        if(inserted) return; // se ho gia' droppato via drag, ignora il click sintetico
        this.polygonPoints.splice(insertAt, 0, { lat: mid.lat, lng: mid.lng });
        if(this.polygon){
          this.polygon.setPaths(this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })));
        }
        this.redrawVertexMarkers();
        this.redrawMidpointMarkers();
        this.updateCentroidMarker();
        this.updateClosedDeleteHandlePosition();
        this.emitPolygonState();
      });

      this.midpointMarkers.push(marker);
    }
  }

  private removeMidpointMarkers() {
    for(const m of this.midpointMarkers){
      m.map = null;
    }
    this.midpointMarkers = [];
  }

  /** Disegna la polyline tratteggiata pre-chiusura. */
  private redrawPolyline() {
    if(this.polygonPolyline){
      this.polygonPolyline.setMap(null);
      this.polygonPolyline = null;
    }
    if(this.polygonPoints.length < 2 || this.polygonClosed) return;
    /* Pattern "dashed" via icons di Polyline: simbolo invisibile ogni 10px,
     * stroke principale impostato a opacita' 0 e simulo i tratti con i symbol. */
    const dashSymbol = {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      strokeColor: POLY_COLOR,
      scale: 3
    };
    this.polygonPolyline = new GoogleMapsService.google.maps.Polyline({
      path: this.polygonPoints.map(p => ({ lat: p.lat, lng: p.lng })),
      strokeColor: POLY_COLOR,
      strokeOpacity: 0,
      strokeWeight: POLY_STROKE_WEIGHT,
      icons: [{ icon: dashSymbol, offset: '0', repeat: '10px' }],
      zIndex: 2,
      clickable: false,
      map: this.map
    });
  }

  /** Risincronizza polygonPoints dal path editato dall'utente (Google Polygon editable). */
  private syncFromPolygonPath() {
    if(!this.polygon) return;
    const path = this.polygon.getPath();
    const pts: { lat: number; lng: number }[] = [];
    for(let i = 0; i < path.getLength(); i++){
      const p = path.getAt(i);
      pts.push({ lat: p.lat(), lng: p.lng() });
    }
    this.polygonPoints = pts;
    this.updateCentroidMarker_safe();
    this.updateClosedDeleteHandlePosition();
    this.emitPolygonState();
  }
  private updateCentroidMarker_safe(){ this.updateCentroidMarker(); }

  /** Aggiorna posizione del marker label sul centroide attuale. */
  private updateCentroidMarker() {
    if(!this.poiMarker) return;
    if(this.polygonPoints.length < 1){
      this.hideLabelMarker();
      return;
    }
    const c = this.calculateCentroid(this.polygonPoints);
    if(!c) return;
    this.showLabelMarker();
    this.poiMarker.position = { lat: c.lat, lng: c.lng };
    // In modalita' poligono il popup "Trascina per spostare" non serve
    // (il marker non si trascina: e' al centroide e segue la geometria).
    if(this.popup) this.popup.setMap(null);
  }

  private hideLabelMarker(){
    if(!this.poiMarker) return;
    this.poiMarker.map = null;
    if(this.popup) this.popup.setMap(null);
  }

  private showLabelMarker(){
    if(!this.poiMarker) return;
    this.poiMarker.map = this.map;
    if(this.popup) this.popup.setMap(this.map);
  }

  /** Crea (se non esiste) e mostra il marker standard di Google sull'indirizzo
   *  corrente. Usato in modalita' poligono pre-chiusura. Usiamo il pin di default
   *  per non confondersi con i pallini blu dei vertici. */
  private showAddressDot(){
    if(!this.map || !this.addressPos) return;
    if(!this.addressDot){
      // Nessun content → AdvancedMarkerElement usa il pin standard di Google
      // (teardrop rosso, chiaramente distinto dai vertici blu del poligono).
      this.addressDot = new GoogleMapsService.google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: this.addressPos,
        zIndex: 2,
        gmpDraggable: false
      });
    }else{
      this.addressDot.position = this.addressPos;
      this.addressDot.map = this.map;
    }
  }

  /** Nasconde/rimuove il pallino dell'indirizzo. */
  private removeAddressDot(){
    if(this.addressDot){
      this.addressDot.map = null;
      // Non lo distruggo del tutto: viene riusato se rientro in fase disegno.
    }
  }

  /** Mostra il pallino rosso con X sul PRIMO vertice quando il poligono e' chiuso.
   *  Click su questo handle → clearPolygon (elimina tutta l'area).
   *  Il marker centroide resta intatto (label/immagine). */
  private installClosedDeleteHandle(){
    if(!this.map || !this.polygonPoints.length || this.closedDeleteHandle) return;

    const size = 22;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative; width:0; height:0;';
    const dot = document.createElement('div');
    dot.title = this.polyLang.cancellaArea;
    dot.style.cssText = [
      'position:absolute',
      `left:-${size/2}px`,
      `bottom:-${size/2}px`,
      `width:${size}px`,
      `height:${size}px`,
      'border-radius:50%',
      'background:#dc2626',
      'border:3px solid #fff',
      'box-shadow:0 2px 8px rgba(0,0,0,0.35)',
      'box-sizing:border-box',
      'cursor:pointer',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'color:#fff',
      'font-weight:900',
      'font-size:13px',
      'line-height:1',
      'padding-bottom:1px'
    ].join(';');
    dot.textContent = '✕';
    wrap.appendChild(dot);

    this.closedDeleteHandle = new GoogleMapsService.google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat: this.polygonPoints[0].lat, lng: this.polygonPoints[0].lng },
      content: wrap,
      zIndex: 6,
      gmpClickable: true,
      gmpDraggable: true
    });
    this.closedDeleteHandle.addListener('gmp-click', () => {
      this.clearPolygon();
    });
    // Drag: sposta il primo vertice. La geometria viene risincronizzata.
    const onDragV0 = (e: any) => {
      if(!e || !e.latLng) return;
      this.polygonPoints[0] = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      if(this.polygon){
        this.polygon.setPaths(this.polygonPoints.map(pp => ({ lat: pp.lat, lng: pp.lng })));
      }
      this.updateCentroidMarker();
      this.redrawMidpointMarkers();
    };
    this.closedDeleteHandle.addListener('drag', onDragV0);
    this.closedDeleteHandle.addListener('dragend', (e: any) => {
      onDragV0(e);
      this.emitPolygonState();
    });
  }

  /** Aggiorna la posizione del closed-delete-handle in seguito a drag dei vertici
   *  via handle nativi del Polygon editable (sync da syncFromPolygonPath). */
  private updateClosedDeleteHandlePosition(){
    if(!this.closedDeleteHandle || !this.polygonPoints.length) return;
    this.closedDeleteHandle.position = {
      lat: this.polygonPoints[0].lat,
      lng: this.polygonPoints[0].lng
    };
  }

  private uninstallClosedDeleteHandle(){
    if(this.closedDeleteHandle){
      this.closedDeleteHandle.map = null;
      this.closedDeleteHandle = null;
    }
  }

  private emitPolygonState() {
    const centroid = this.calculateCentroid(this.polygonPoints);
    const centroidValid = (centroid && this.polygonClosed && this.polygonPoints.length >= 3)
      ? this.isPointInPolygon(centroid, this.polygonPoints)
      : null;
    this.polygonState$.next({
      pointsCount: this.polygonPoints.length,
      closed: this.polygonClosed,
      centroid,
      centroidValid
    });
  }
}
